import { doLayout } from '@/modules/admin/workflows/lib/elk.old'
import { createEdge, wizardBlocksToNodesAndEdges } from '@/modules/admin/workflows/lib/reactflow'
import { actionPlaceholder, conditionBlockPlaceholder } from '@/modules/admin/workflows/store/data'
import useStore from '@/modules/admin/workflows/store/store'
import { INodeType } from '@/modules/admin/workflows/types/wizard-flow'
import { IWizard } from '@/modules/admin/workflows/types/wizard-types'
import { useQueryFlowEntities } from '@/services/flow-entities/queries'
import { FlowEntityType, IFlowEntity } from '@/services/flow-entities/types'
import { useQueryWizard } from '@/services/wizard/queries'
import React, { createContext, useEffect, useMemo, useState } from 'react'
import { useLocation, useParams } from 'react-router'
import { v4 } from 'uuid'

/* eslint-disable react-refresh/only-export-components -- context module */

export enum BlockSelectionSidebarDisplay {
    TRIGGER = 'trigger',
    ACTION = 'action',
    UTILITY = 'utility',
    CONDITION = 'condition',
}

export type UnmatchedVariables = {
    [key: string]: { source: string; value: string; type: string }[]
}

export type BlockSelectionSidebarState = {
    isOpen: boolean
    display: BlockSelectionSidebarDisplay[]
    parentId?: string | null
    prevNodeId?: string | null
}

type SequenceContextType = {
    wizard: IWizard | null
    flowEntities: IFlowEntity[]
    blockSelectionSidebar: BlockSelectionSidebarState
    setBlockSelectionSidebar: (state: BlockSelectionSidebarState) => void
    isWizardLoading: boolean
    unmatchedVariables: UnmatchedVariables
    setUnmatchedVariables: (variables: UnmatchedVariables) => void
    openValidationSheet: boolean
    setOpenValidationSheet: (open: boolean) => void
    openSchemasSheet: boolean
    setOpenSchemasSheet: (open: boolean) => void
}

const defaultContext: SequenceContextType = {
    wizard: null,
    flowEntities: [],
    blockSelectionSidebar: {
        isOpen: false,
        display: [],
        parentId: null,
        prevNodeId: null,
    },
    setBlockSelectionSidebar: () => {},
    isWizardLoading: false,
    unmatchedVariables: {},
    setUnmatchedVariables: () => {},
    openValidationSheet: false,
    setOpenValidationSheet: () => {},
    openSchemasSheet: false,
    setOpenSchemasSheet: () => {},
}

export const SequenceContext = createContext(defaultContext)

export const SequenceContextProvider = ({ children }: { children: React.ReactNode }) => {
    const params = useParams<{ slug: string }>()
    const path = useLocation().pathname
    const [blockSelectionSidebar, setBlockSelectionSidebar] = useState<BlockSelectionSidebarState>({
        isOpen: false,
        display: [],
        parentId: null,
        prevNodeId: null,
    })
    const [unmatchedVariables, setUnmatchedVariables] = useState<UnmatchedVariables>({})
    const [openValidationSheet, setOpenValidationSheet] = useState(false)
    const [openSchemasSheet, setOpenSchemasSheet] = useState(false)

    const { data: flowEntities } = useQueryFlowEntities({
        queryParams: {
            limit: '200',
            page: '1',
        },
    })

    const { data: wizard, isLoading: isWizardLoading } = useQueryWizard({
        enabled: !!params.slug && path.includes('sequences') && path.includes('admin'),
        queryParams: {
            slug: params.slug as string,
        },
    })

    const { setNodes, setEdges } = useStore()

    const handleLayout = async () => {
        if (!wizard || !wizard.blocks) return

        const flowElements = wizardBlocksToNodesAndEdges(wizard.blocks)

        const rootNodes = flowElements.nodes.filter(node => !node.parentId)
        const qualifyingChildNodes = flowElements.nodes.filter(
            node => node.parentId && node.type === INodeType.CONDITION
        )

        if (rootNodes.length > 0) {
            const nodesWithoutNextNode = [...rootNodes, ...qualifyingChildNodes].filter(
                node => !node.data.next && node.type !== INodeType.CONDITION_GROUP
            )

            for (const node of nodesWithoutNextNode) {
                const placeholderId = v4()
                flowElements.nodes.push({
                    ...actionPlaceholder,
                    id: placeholderId,
                })
                const edge = createEdge(node.id, placeholderId)
                flowElements.edges.push(edge)
            }
        }

        for (const node of flowElements.nodes) {
            if (node.type === INodeType.CONDITION_GROUP) {
                flowElements.nodes.push({
                    ...conditionBlockPlaceholder,
                    id: `${node.id}-placeholder`,
                    extent: 'parent',
                    parentId: node.id,
                    position: { x: 0, y: 0 },
                })
            }
        }

        // Handle loop nodes
        for (const node of flowElements.nodes) {
            if (node.type === INodeType.LOOP) {
                const placeholderId = `${node.id}-placeholder`
                flowElements.nodes.push({
                    ...actionPlaceholder,
                    id: placeholderId,
                    extent: 'parent',
                    parentId: node.id,
                    position: { x: 0, y: 0 },
                })

                const children = flowElements.nodes.filter(
                    n => n.parentId === node.id && n.type !== INodeType.PLACEHOLDER_NODE
                )

                if (children.length > 0) {
                    const lastChild = children.find(child => !child.data.next)
                    if (lastChild) {
                        flowElements.edges.push(createEdge(lastChild.id, placeholderId))
                    }
                }
            }
        }

        const layoutedElements = await doLayout(flowElements.nodes, flowElements.edges)

        if (layoutedElements.nodes.length) {
            setNodes(layoutedElements.nodes)
            setEdges(layoutedElements.edges)
        }
    }

    useEffect(() => {
        if (wizard && wizard.blocks) {
            handleLayout()
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [wizard])

    const filteredFlowEntities = useMemo(() => {
        if (!flowEntities?.data) return []
        return flowEntities.data.filter(
            entity => !(blockSelectionSidebar.parentId && entity.type === FlowEntityType.LOOP)
        )
    }, [flowEntities?.data, blockSelectionSidebar.parentId])

    const value = useMemo(
        () => ({
            wizard: wizard || null,
            flowEntities: filteredFlowEntities,
            blockSelectionSidebar,
            setBlockSelectionSidebar,
            isWizardLoading,
            unmatchedVariables,
            setUnmatchedVariables,
            openValidationSheet,
            setOpenValidationSheet,
            openSchemasSheet,
            setOpenSchemasSheet,
        }),
        [
            wizard,
            filteredFlowEntities,
            blockSelectionSidebar,
            setBlockSelectionSidebar,
            isWizardLoading,
            unmatchedVariables,
            openValidationSheet,
            openSchemasSheet,
        ]
    )

    return <SequenceContext.Provider value={value}>{children}</SequenceContext.Provider>
}
